import {
  Component,
  input,
  output,
  ViewChild,
  forwardRef,
  AfterViewInit,
  OnDestroy,
} from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { QuillEditorComponent, QuillModule } from 'ngx-quill';
import Quill from 'quill';

/**
 * Custom Rich Text Input Component
 *
 * A reusable rich text editor component that implements Angular's ControlValueAccessor interface
 * for seamless integration with Angular Forms (both Template-driven and Reactive).
 *
 * This component uses Quill.js via ngx-quill for rich text editing capabilities
 * while maintaining visual consistency with other form controls (textarea-input).
 *
 * @example
 * // Template-driven form
 * <app-rich-text-input
 *   label="Description"
 *   placeholder="Enter description"
 *   [(ngModel)]="description"
 * ></app-rich-text-input>
 *
 * @example
 * // Reactive form
 * <app-rich-text-input
 *   label="Notes"
 *   [formControl]="notesControl"
 *   helperText="Enter any additional notes"
 * ></app-rich-text-input>
 */
@Component({
  selector: 'app-rich-text-input',
  templateUrl: './rich-text-input.component.html',
  styleUrls: ['./rich-text-input.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, QuillModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RichTextInputComponent),
      multi: true,
    },
  ],
})
export class RichTextInputComponent implements ControlValueAccessor, AfterViewInit, OnDestroy {
  /**
   * Reference to the Quill editor component
   */
  @ViewChild(QuillEditorComponent, { static: false }) quillEditor?: QuillEditorComponent;

  // ========================================
  // Custom Styling
  // ========================================

  /**
   * Enable theme shadow on editor
   * @default false
   */
  readonly shadow = input<boolean>(false);

  /**
   * Custom CSS class to apply
   */
  readonly customClass = input<string | undefined>(undefined);

  /**
   * Unique ID for the editor element
   */
  readonly id = input<string>(`rich-text-${Math.random().toString(36).substr(2, 9)}`);

  // ========================================
  // Label & Helper Text
  // ========================================

  /**
   * Label text displayed above the editor
   */
  readonly label = input<string | undefined>(undefined);

  /**
   * Helper text displayed below the editor
   * Useful for providing additional guidance
   */
  readonly helperText = input<string | undefined>(undefined);

  /**
   * Error message text displayed below the editor
   * Takes precedence over helper text when present
   */
  readonly errorText = input<string | undefined>(undefined);

  // ========================================
  // Editor Behavior
  // ========================================

  /**
   * Placeholder text shown when editor is empty
   */
  readonly placeholder = input<string | undefined>('Enter text...');

  /**
   * Disable the editor
   * @default false
   */
  disabled: boolean = false;

  /**
   * Make the editor readonly
   * @default false
   */
  readonly readonly = input<boolean>(false);

  /**
   * Mark the editor as required
   * @default false
   */
  readonly required = input<boolean>(false);

  /**
   * Minimum height for the editor (in pixels)
   * @default 150
   */
  readonly minHeight = input<number>(150);

  /**
   * Maximum height for the editor (in pixels)
   */
  readonly maxHeight = input<number | undefined>(undefined);

  /**
   * Quill editor theme
   * @default 'snow'
   */
  readonly theme = input<'snow' | 'bubble'>('snow');

  /**
   * Quill toolbar configuration
   * @default basic toolbar with common formatting options
   */
  readonly toolbar = input<any>([
    ['bold', 'italic', 'underline', 'strike'],
    ['blockquote', 'code-block'],
    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
    [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
    ['link'],
    ['clean'],
  ]);

  /**
   * Quill editor formats
   */
  readonly formats = input<string[]>(['bold', 'italic', 'underline', 'strike', 'blockquote', 'code-block', 'list', 'bullet', 'header', 'link']);

  // ========================================
  // Events
  // ========================================

  /**
   * Emitted when the editor value changes
   */
  readonly valueChange = output<string>();

  /**
   * Emitted when the editor gains focus
   */
  readonly focused = output<any>();

  /**
   * Emitted when the editor loses focus
   */
  readonly blurred = output<any>();

  /**
   * Emitted when the editor is created
   */
  readonly editorCreated = output<Quill>();

  // ========================================
  // ControlValueAccessor Properties
  // ========================================

  /**
   * Current value of the editor (HTML string)
   */
  value: string = '';

  /**
   * Callback function to call when value changes
   * Part of ControlValueAccessor interface
   */
  private onChange: (value: string) => void = () => {};

  /**
   * Callback function to call when editor is touched
   * Part of ControlValueAccessor interface
   */
  private onTouched: () => void = () => {};

  /**
   * Quill editor instance
   */
  private quillInstance?: Quill;

  // ========================================
  // Quill Configuration
  // ========================================

  /**
   * Quill modules configuration
   */
  get quillModules() {
    return {
      toolbar: this.toolbar(),
      clipboard: {
        matchVisual: false,
      },
    };
  }

  // ========================================
  // Lifecycle Hooks
  // ========================================

  ngAfterViewInit(): void {
    // Additional initialization if needed
  }

  ngOnDestroy(): void {
    // Cleanup if needed
  }

  // ========================================
  // ControlValueAccessor Methods
  // ========================================

  /**
   * Write a new value to the element
   * Called by Angular Forms when programmatically setting the value
   */
  writeValue(value: string): void {
    this.value = value || '';
    // ngModel binding will handle updating the editor content
  }

  /**
   * Register a callback function that is called when the value changes
   * Called by Angular Forms to set up the change handler
   */
  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  /**
   * Register a callback function that is called when the element is touched
   * Called by Angular Forms to set up the touch handler
   */
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  /**
   * Set the disabled state of the element
   * Called by Angular Forms when the disabled state changes
   */
  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    
    if (this.quillInstance) {
      this.quillInstance.enable(!isDisabled);
    }
  }

  // ========================================
  // Event Handlers
  // ========================================

  /**
   * Handle editor creation
   */
  onEditorCreated(quill: Quill): void {
    this.quillInstance = quill;
    
    // Set disabled state
    if (this.disabled || this.readonly()) {
      quill.enable(false);
    }
    
    this.editorCreated.emit(quill);
  }

  /**
   * Handle content changes
   */
  onContentChanged(event: { html: string | null }): void {
    const html = event.html || '';
    
    // Quill returns '<p><br></p>' for empty content
    // Treat this as empty string
    const isEmpty = html === '<p><br></p>' || html === '';
    this.value = isEmpty ? '' : html;
    
    this.onChange(this.value);
    this.valueChange.emit(this.value);
  }

  /**
   * Handle editor focus event
   */
  onFocusChange(event: unknown): void {
    this.focused.emit(event);
  }

  /**
   * Handle editor blur event
   */
  onBlurChange(event: unknown): void {
    this.onTouched();
    this.blurred.emit(event);
  }

  // ========================================
  // Public Methods
  // ========================================

  /**
   * Programmatically focus the editor
   */
  setFocus(): void {
    if (this.quillInstance) {
      this.quillInstance.focus();
    }
  }

  /**
   * Programmatically blur the editor
   */
  setBlur(): void {
    if (this.quillInstance) {
      this.quillInstance.blur();
    }
  }

  /**
   * Get the Quill editor instance
   */
  getEditorInstance(): Quill | undefined {
    return this.quillInstance;
  }

  /**
   * Get the plain text content (without HTML)
   */
  getPlainText(): string {
    if (this.quillInstance) {
      return this.quillInstance.getText();
    }
    return '';
  }

  /**
   * Clear the editor content
   */
  clear(): void {
    if (this.quillInstance) {
      this.quillInstance.setText('');
      this.value = '';
      this.onChange(this.value);
      this.valueChange.emit(this.value);
    }
  }
}

